import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BedDouble, ClipboardList, HeartPulse, Package, ShieldCheck, Users, Wallet } from 'lucide-react';
import type { AdmissionCareTeamDto, AdmissionPreAuthStatusDto, PatientPackageView } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn, dateOnly, dateTime, money, timeOnly } from '@/lib/utils';
import { Badge, StatusBadge } from '@/components/ui/primitives';
import { ShellSummaryCard } from '@/components/clinical-shell/ShellPanel';
import { careRoleLabel } from '../AdmissionPatientBar';
import { ipdEmrPath, type Admission } from '../types';
import { vitalsLine } from '../vitals-line';
import type { WorkspaceSectionId } from '../workspace-sections';
import { PreAuthBadge } from './PreAuthSection';
import { preAuthState } from '../workspace-facts';

function Figure({ label, value, tone }: { label: string; value: ReactNode; tone?: 'destructive' | 'success' }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn('tabular text-base font-semibold', tone === 'destructive' && 'text-destructive', tone === 'success' && 'text-success')}>{value}</dd>
    </div>
  );
}

function EmrLink({ admissionId, section, children }: { admissionId: string; section: string; children: ReactNode }) {
  return (
    <Link to={ipdEmrPath(admissionId, section)} className="inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline">
      {children} <ArrowUpRight className="h-3.5 w-3.5 rtl:-scale-x-100" aria-hidden />
    </Link>
  );
}

export function WorkspaceOverview({
  admission,
  team,
  preAuth,
  insurance,
  packages,
  available,
  onOpen,
}: {
  admission: Admission;
  team: AdmissionCareTeamDto | undefined;
  preAuth: AdmissionPreAuthStatusDto | undefined;
  insurance: boolean;
  packages: readonly PatientPackageView[] | undefined;
  available: readonly WorkspaceSectionId[];
  onOpen: (id: WorkspaceSectionId) => void;
}) {
  const opener = (id: WorkspaceSectionId) => (available.includes(id) ? () => onOpen(id) : undefined);
  const account = admission.account;
  const work = admission.openWork ?? { labItems: 0, radiology: 0, dueDoses: 0 };
  const latest = admission.vitals[0];
  const readings = vitalsLine(latest);
  const livePackages = (packages ?? []).filter((row) => row.status !== 'cancelled');
  const members = team?.members ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ShellSummaryCard icon={Wallet} title={tr('ipd', 'ws_account_position')} onOpen={opener('bill')} className="lg:col-span-2">
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Figure label={tr('ipd', 'billed')} value={money(account.billedMinor)} />
          <Figure label={tr('ipd', 'advances_payments')} value={money(account.depositedMinor + account.paidMinor)} />
          <Figure
            label={account.balanceMinor < 0 ? tr('ipd', 'owing') : tr('ipd', 'credit')}
            value={money(Math.abs(account.balanceMinor))}
            tone={account.balanceMinor < 0 ? 'destructive' : 'success'}
          />
          <Figure label={tr('ipd', 'ws_payer')} value={<span className="text-sm">{admission.payer?.name ?? tr('ipd', 'ws_self_pay')}</span>} />
        </dl>
      </ShellSummaryCard>

      <ShellSummaryCard icon={ClipboardList} title={tr('ipd', 'ws_open_work')} aside={<EmrLink admissionId={admission.id} section="overview">{tr('ipd', 'ws_open_ipd_emr')}</EmrLink>}>
        <dl className="grid grid-cols-3 gap-3">
          <Figure label={tr('ipd', 'ws_lab_tests')} value={work.labItems} />
          <Figure label={tr('ipd', 'ws_imaging')} value={work.radiology} />
          <Figure label={tr('ipd', 'ws_doses_due')} value={work.dueDoses} />
        </dl>
      </ShellSummaryCard>

      <ShellSummaryCard icon={HeartPulse} title={tr('ipd', 'ws_latest_vitals')} aside={<EmrLink admissionId={admission.id} section="vitals">{tr('ipd', 'vitals')}</EmrLink>}>
        {readings.length > 0 ? (
          <>
            <dl className="grid grid-cols-3 gap-3 sm:grid-cols-5">
              {readings.map((reading) => (
                <Figure key={reading.key} label={reading.label} value={reading.value} />
              ))}
            </dl>
            <p className="mt-2 text-xs text-muted-foreground">{tr('ipd', 'ws_taken_at_x', { time: `${dateOnly(latest?.recordedAt)} ${timeOnly(latest?.recordedAt)}` })}</p>
          </>
        ) : (
          <p className="text-muted-foreground">{tr('ipd', 'no_vitals_recorded')}</p>
        )}
      </ShellSummaryCard>

      <ShellSummaryCard icon={Users} title={tr('ipd', 'ws_care_team')} onOpen={opener('care-team')} aside={members.length > 0 ? <Badge variant="muted">{members.length}</Badge> : undefined}>
        <ul className="space-y-1.5">
          {(members.length > 0 ? members : [{ id: admission.doctor.id, name: admission.doctor.name, role: 'primary_consultant' as const }]).map((member) => (
            <li key={member.id} className="flex items-center justify-between gap-2">
              <span className="truncate font-medium">{member.name ?? tr('ipd', 'ws_unknown_doctor')}</span>
              <Badge variant={member.role === 'primary_consultant' ? 'default' : 'secondary'}>{careRoleLabel(member.role)}</Badge>
            </li>
          ))}
        </ul>
      </ShellSummaryCard>

      <ShellSummaryCard icon={BedDouble} title={tr('ipd', 'ws_stay')} onOpen={opener('bed')}>
        <dl className="space-y-1.5">
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{tr('ipd', 'ws_admitted')}</dt>
            <dd className="text-end">{dateTime(admission.admittedAt)}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{tr('ipd', 'ws_bed')}</dt>
            <dd className="text-end">{admission.bed ? `${admission.bed.room.ward.name} · ${admission.bed.code}` : tr('ipd', 'ws_no_bed')}</dd>
          </div>
          {admission.provisionalDiagnosis && (
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{tr('ipd', 'ws_diagnosis')}</dt>
              <dd className="line-clamp-2 text-end">{admission.provisionalDiagnosis}</dd>
            </div>
          )}
        </dl>
      </ShellSummaryCard>

      {insurance && (
        <ShellSummaryCard icon={ShieldCheck} title={tr('ipd', 'ws_nav_preauth')} onOpen={opener('preauth')} aside={<PreAuthBadge state={preAuthState(admission, preAuth)} />}>
          {admission.payer ? (
            <p>
              {preAuth?.approvedUntil
                ? tr('ipd', 'ws_approved_until_x', { date: dateOnly(preAuth.approvedUntil) })
                : (preAuth?.message ?? tr('ipd', `ws_preauth_${preAuthState(admission, preAuth)}`))}
            </p>
          ) : (
            <p className="text-muted-foreground">{tr('ipd', 'ws_preauth_self_pay_desc')}</p>
          )}
        </ShellSummaryCard>
      )}

      {packages !== undefined && (
        <ShellSummaryCard icon={Package} title={tr('ipd', 'ws_nav_packages')} onOpen={opener('packages')}>
          {livePackages.length === 0 ? (
            <p className="text-muted-foreground">{tr('ipd', 'ws_no_package')}</p>
          ) : (
            <ul className="space-y-1.5">
              {livePackages.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{row.name}</span>
                  <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    {tr('ipd', 'ws_used_x_of_x', { used: money(row.consumedValueMinor), price: money(row.priceMinor) })}
                    <StatusBadge status={row.status} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </ShellSummaryCard>
      )}
    </div>
  );
}
